import Phaser from 'phaser';

/**
 * AvatarSprite — Sprite joueur basé sur characters.webp.
 *
 * ┌─────────────────────────────────────────────────────────────────────────┐
 * │  characters.webp  192×128px — frames 16×16px — grille 12 col × 8 lignes │
 * │  8 personnages (P0-P7) : P0-P3 rangée haute, P4-P7 rangée basse        │
 * │                                                                         │
 * │  Chaque personnage occupe un bloc 3 col × 4 lignes (12 frames) :        │
 * │    Col+0 : pied gauche devant                                            │
 * │    Col+1 : neutre                                                        │
 * │    Col+2 : pied droit devant                                             │
 * │    Lig+0  (base+0)  : face / bas                                         │
 * │    Lig+1  (base+12) : profil gauche                                      │
 * │    Lig+2  (base+24) : profil droit                                       │
 * │    Lig+3  (base+36) : dos / haut                                         │
 * │                                                                         │
 * │  base(P) = Math.floor(P/4)*4*12 + (P%4)*3                              │
 * │  Personnage sélectionné : abs(djb2(userId)) % 8                         │
 * └─────────────────────────────────────────────────────────────────────────┘
 *
 * Utilise le système d'animation Phaser (anims.play) :
 *   registerAvatarAnimations(scene) crée les 64 animations (8 chars × 8 anims)
 *   à appeler UNE FOIS dans finishCreate() avant de créer les sprites.
 *
 * Clés d'animation : '{walk|idle}-{down|left|right|up}-{charIdx}'
 */

export const AVATAR_CHAR_COUNT = 8;   // P0-P7 (4 rangée haute + 4 rangée basse)

// Nombre de colonnes dans la grille du spritesheet
const SHEET_COLS = 12;

// ── Calcul de base frame ──────────────────────────────────────────────────────

/** Index du premier frame d'un personnage dans le spritesheet. */
export function charBase(charIdx: number): number {
  return Math.floor(charIdx / 4) * 4 * SHEET_COLS + (charIdx % 4) * 3;
}

// ── Enregistrement des animations ────────────────────────────────────────────

/**
 * Crée les animations Phaser pour tous les personnages.
 * Idempotent : ne recrée pas si elles existent déjà.
 * À appeler dans ZoneScene.finishCreate() après le chargement du spritesheet.
 */
export function registerAvatarAnimations(scene: Phaser.Scene): void {
  if (scene.anims.exists('idle-down-0')) return;

  for (let p = 0; p < AVATAR_CHAR_COUNT; p++) {
    const b = charBase(p);

    // ── walk : 3 frames cycliques (pied gauche → neutre → pied droit) ──
    scene.anims.create({
      key: `walk-down-${p}`,
      frames:    scene.anims.generateFrameNumbers('avatars', { frames: [b + 0,  b + 1,  b + 2]  }),
      frameRate: 8, repeat: -1,
    });
    scene.anims.create({
      key: `walk-left-${p}`,
      frames:    scene.anims.generateFrameNumbers('avatars', { frames: [b + 12, b + 13, b + 14] }),
      frameRate: 8, repeat: -1,
    });
    scene.anims.create({
      key: `walk-right-${p}`,
      frames:    scene.anims.generateFrameNumbers('avatars', { frames: [b + 24, b + 25, b + 26] }),
      frameRate: 8, repeat: -1,
    });
    scene.anims.create({
      key: `walk-up-${p}`,
      frames:    scene.anims.generateFrameNumbers('avatars', { frames: [b + 36, b + 37, b + 38] }),
      frameRate: 8, repeat: -1,
    });

    // ── idle : frame neutre (col+1) — image fixe ──
    scene.anims.create({
      key: `idle-down-${p}`,
      frames:    scene.anims.generateFrameNumbers('avatars', { frames: [b + 1]  }),
      frameRate: 1, repeat: 0,
    });
    scene.anims.create({
      key: `idle-left-${p}`,
      frames:    scene.anims.generateFrameNumbers('avatars', { frames: [b + 13] }),
      frameRate: 1, repeat: 0,
    });
    scene.anims.create({
      key: `idle-right-${p}`,
      frames:    scene.anims.generateFrameNumbers('avatars', { frames: [b + 25] }),
      frameRate: 1, repeat: 0,
    });
    scene.anims.create({
      key: `idle-up-${p}`,
      frames:    scene.anims.generateFrameNumbers('avatars', { frames: [b + 37] }),
      frameRate: 1, repeat: 0,
    });
  }
}

// ── Classe ────────────────────────────────────────────────────────────────────

// Couleurs de statut en hex Phaser (nombre, pas CSS)
const STATUS_HEX: Record<string, number> = {
  available: 0x22c55e,
  away:      0xeab308,
  busy:      0xef4444,
  focus:     0x7F77DD,
};

export class AvatarSprite extends Phaser.GameObjects.Sprite {
  /** Index du personnage (0–7) — utilisé par ZoneScene pour construire la clé d'anim. */
  charIdx: number;
  private readonly nameBg: Phaser.GameObjects.Rectangle;
  private readonly nameLabel: Phaser.GameObjects.Text;
  private readonly statusDot: Phaser.GameObjects.Arc;
  private readonly muteBg: Phaser.GameObjects.Rectangle;
  private readonly muteLabel: Phaser.GameObjects.Text;
  private _isMuted = false;
  private _muteTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    userId = '',
    explicitCharIdx?: number,
    displayName = '',
  ) {
    const charIdx = explicitCharIdx !== undefined
      ? Math.abs(explicitCharIdx) % AVATAR_CHAR_COUNT
      : charIdxFromUserId(userId);
    // Frame initial : neutre, face bas (col+1 de la lig+0)
    super(scene, x, y, 'avatars', charBase(charIdx) + 1);
    this.charIdx = charIdx;
    this.setScale(2);
    scene.add.existing(this);

    // ── Fond semi-transparent derrière le nom ────────────────
    this.nameBg = scene.add.rectangle(x, y, 10, 18, 0x000000, 0.55)
      .setOrigin(0.5, 1);

    // ── Label nom — police 14px, stroke blanc pour contraste ──
    this.nameLabel = scene.add.text(x, y, displayName, {
      fontSize: '14px',
      color: '#ffffff',
      stroke: '#ffffff',
      strokeThickness: 1,
      resolution: 2,
    }).setOrigin(0.5, 1);

    // ── Pastille de statut (6px) à droite du nom ──────────────
    this.statusDot = scene.add.circle(x, y, 4, STATUS_HEX['available'])
      .setOrigin(0, 0.5);

    // ── Badge MUTE avec fond rouge (masqué par défaut) ────────
    this.muteBg = scene.add.rectangle(x, y, 36, 14, 0x7f1d1d, 0.7)
      .setOrigin(0.5, 1).setVisible(false);
    this.muteLabel = scene.add.text(x, y, 'MUTE', {
      fontSize: '10px',
      color: '#ffffff',
      stroke: '#ffffff',
      strokeThickness: 1,
      resolution: 2,
    }).setOrigin(0.5, 1).setVisible(false);

    // Enregistrer dans l'updateList pour que preUpdate() soit appelé chaque frame
    scene.sys.updateList.add(this);
  }

  setDisplayName(name: string): void {
    this.nameLabel.setText(name);
  }

  /** Change l'avatar en mettant à jour l'index et le frame courant. */
  setAvatarIndex(index: number): void {
    this.charIdx = Math.abs(index) % AVATAR_CHAR_COUNT;
    this.setFrame(charBase(this.charIdx) + 1);
    this.play(`idle-down-${this.charIdx}`);
  }

  /** Met à jour la couleur de la pastille de statut. */
  setStatus(status: string): void {
    this.statusDot.setFillStyle(STATUS_HEX[status] ?? STATUS_HEX['available']);
  }

  /** Affiche ou masque le badge MUTE au-dessus de l'avatar. */
  setMuted(muted: boolean): void {
    this._isMuted = muted;
    this.muteBg.setVisible(muted);
    this.muteLabel.setVisible(muted);

    // Clear tout timer précédent
    if (this._muteTimer) {
      clearTimeout(this._muteTimer);
      this._muteTimer = null;
    }

    // Auto-hide après 2 minutes
    if (muted) {
      this._muteTimer = setTimeout(() => {
        this._isMuted = false;
        this.muteBg.setVisible(false);
        this.muteLabel.setVisible(false);
        this._muteTimer = null;
      }, 120_000);
    }
  }

  /** Sync la position du label, fond et pastille chaque frame. */
  override preUpdate(time: number, delta: number): void {
    super.preUpdate(time, delta);
    const nameY = this.y - this.displayHeight / 2 - 3;

    // Nom
    this.nameLabel.setPosition(this.x, nameY);
    this.nameLabel.setDepth(this.depth + 2);

    // Fond dimensionné au texte + padding
    const tw = this.nameLabel.width;
    const th = this.nameLabel.height;
    this.nameBg.setPosition(this.x, nameY);
    this.nameBg.setSize(tw + 16, th + 4);
    this.nameBg.setDepth(this.depth + 1);

    // Pastille de statut — à droite du fond
    this.statusDot.setPosition(this.x + tw / 2 + 5, nameY - th / 2);
    this.statusDot.setDepth(this.depth + 2);

    // Badge MUTE — au-dessus du nom avec espacement
    if (this._isMuted) {
      const muteY = nameY - th - 4;
      this.muteBg.setPosition(this.x, muteY);
      this.muteBg.setSize(this.muteLabel.width + 10, this.muteLabel.height + 4);
      this.muteBg.setDepth(this.depth + 3);
      this.muteLabel.setPosition(this.x, muteY);
      this.muteLabel.setDepth(this.depth + 4);
    }
  }

  override destroy(fromScene?: boolean): void {
    if (this._muteTimer) clearTimeout(this._muteTimer);
    this.nameBg.destroy();
    this.nameLabel.destroy();
    this.statusDot.destroy();
    this.muteBg.destroy();
    this.muteLabel.destroy();
    super.destroy(fromScene);
  }
}

// ── Utilitaires ───────────────────────────────────────────────────────────────

/** Hash djb2 du userId → index de personnage dans [0, AVATAR_CHAR_COUNT[. */
function charIdxFromUserId(userId: string): number {
  let h = 5381;
  for (let i = 0; i < userId.length; i++) {
    h = ((h << 5) + h) ^ userId.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h) % AVATAR_CHAR_COUNT;
}
